import React from 'react';
import { gql } from '@soundxyz/gql-string';
import type { CSS } from '@components-shared/src/stitches';
import { SeeAllLink } from '@components/basics/SeeAll';
import { SeeAllArrow } from '@components/basics/SeeAllArrow';
import type { Pathname } from '@components/main-components/link/AccessibleLink';
import { MediaQuery } from '@context/mediaQuery';
import type { ParamsValues } from '@context/params';
import type { FragmentType } from '@graphql/reactQuery';
import { CarouselUserInfoFragmentDoc, getFragment } from '@graphql/reactQuery';
import { GridCarousel } from './GridCarousel';
import { CarouselContainer, Header, PlaylistInfo } from './shared';

// Max number of slides visible at once + 1 for a buffer
const RENDERED_SLIDE_COUNT = 5;
const RENDERED_SLIDE_COUNT_MOBILE = 2;

export const CarouselHeader = ({
  subheader,
  header,
  user: userFragment,
  path,
  seeAllParams,
  hideSeeAll = false,
}: {
  header: string;
  subheader: string | React.ReactNode;
  user: FragmentType<CarouselUserInfoFragmentDoc>;
  path?: Pathname;
  seeAllParams: ParamsValues | null;
  hideSeeAll?: boolean;
}) => {
  const user = getFragment(CarouselUserInfoFragmentDoc, userFragment);

  return (
    <Header>
      <PlaylistInfo>
        <h3>{header}</h3>
        <p>{subheader}</p>
      </PlaylistInfo>
      {!hideSeeAll && path && (
        <>
          <SeeAllLink user={user} path={path} params={seeAllParams} className="hidden md:flex" />
          <SeeAllArrow
            user={user}
            path={path}
            params={seeAllParams}
            className="my-3 ml-3 h-6 md:hidden"
          />
        </>
      )}
    </Header>
  );
};

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment CarouselUserInfo on User {
    id
    ...SeeAllUserInfo
  }
`);

export const CarouselWithHeader = ({
  header,
  subheader,
  slides,
  path,
  seeAllParams,
  css,
  loadMore,
  user: userFragment,
}: {
  user: FragmentType<CarouselUserInfoFragmentDoc>;
  header: string;
  subheader?: string | React.ReactNode;
  slides: { id: string; slide: JSX.Element }[];
  path: Pathname;
  seeAllParams: ParamsValues | null;
  css?: CSS;
  loadMore?: () => void;
}) => {
  const { tablet } = MediaQuery.useContainer();

  const onLastPageReached = () => {
    if (!loadMore) return;

    loadMore();
  };

  // Do not show See All button if there are less than 5 and less than 2 on mobile
  const hideSeeAll = tablet
    ? slides.length < RENDERED_SLIDE_COUNT
    : slides.length < RENDERED_SLIDE_COUNT_MOBILE;

  return (
    <CarouselContainer css={css}>
      <CarouselHeader
        header={header}
        subheader={subheader}
        user={userFragment}
        path={path}
        seeAllParams={seeAllParams}
        hideSeeAll={hideSeeAll}
      />
      <GridCarousel
        onEndReached={onLastPageReached}
        slides={slides}
        onInternalActiveIndexChange={null}
        columnAmount={null}
        hideArrows={false}
        externalActiveIndex={null}
        spacing={null}
        alignSlidesLeft={false}
        ssrSlideWidth={undefined}
        columnType="default"
      />
    </CarouselContainer>
  );
};
